/**
 * useCarrierSequence — manages carrier column ordering state for SBS pages.
 * Provides drag-and-drop reordering, preset sort strategies, and
 * current-plan anchoring validation.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { arrayMove } from "@dnd-kit/sortable";
import { logger } from "@platform/browser-logger";

import {
  applyCarrierSequencePreset,
  extractCarrierSortInfo,
  projectCarrierOrder,
} from "../adapters/carrierSequenceUtils";
import {
  CarrierSequencePreset,
  type CarrierSequenceOrder,
} from "../types/carrierSequence.types";

import {
  carrierReorderReducer,
  createInitialCarrierReorderState,
  type CarrierChangeSource,
} from "./carrierReorderReducer";

import type { Proposal } from "../types/proposal.types";

export type { CarrierChangeSource };

export interface UseCarrierSequenceOptions {
  proposals: Proposal[];
  coverageType: string;
  /** Pre-populated carrier order (e.g. from shared context / sessionStorage). */
  initialOrder?: CarrierSequenceOrder;
  onOrderChange?: (
    carrierIds: string[],
    source: "drag" | "preset",
    movedCarrierId: string | null,
  ) => void;
  /**
   * Carrier ID that must always remain at position 0 (e.g. the synthetic
   * Incumbent/Requested carrier in the Quotes SBS grid). When provided:
   * - Any reorder that displaces this carrier is rejected with a validation error.
   * - This ID is stripped from the order saved to sequenceOrder so that
   *   carrierIdOrder only contains real carrier IDs (the locked carrier is
   *   always prepended separately when building grid data).
   */
  lockedLeadingCarrierId?: string;
  /**
   * Cross-LOC canonical carrier order (e.g. CarrierSequenceContext's
   * appliedToAllCarrierOrder), used only as a fallback when this coverage
   * type has no custom order of its own — so a carrier dragged to a given
   * position on one LOC shows up in the same relative position on any other
   * LOC it also quotes, instead of each LOC falling back to its own
   * independent default sort. Never written to directly by this hook; only
   * an explicit drag/preset (via onOrderChange) should ever update it.
   */
  canonicalOrder?: string[];
}

export interface UseCarrierSequenceReturn {
  carrierIdOrder: string[];
  appliedPreset: CarrierSequencePreset | null;
  handleDragReorder: (fromIndex: number, toIndex: number) => void;
  setCarrierOrder: (
    carrierIds: string[],
    movedCarrierId?: string | null,
  ) => void;
  applyPreset: (preset: CarrierSequencePreset) => void;
  resetOrder: () => void;
  isCustomOrder: boolean;
  /** Whether carrierIdOrder reflects a cross-LOC canonical-order projection (see canonicalOrder). */
  isProjectedOrder: boolean;
  lastValidationError: string | null;
  clearValidationError: () => void;
  /** Whether a reorder is awaiting confirmation. */
  hasPendingOrder: boolean;
  /** Commit the pending reorder so it becomes the active order. */
  commitPendingOrder: () => void;
  /** Discard the pending reorder and keep the previous order. */
  revertPendingOrder: () => void;
  /** Indicates whether the most recent committed order change came from a drag or preset. */
  lastChangeSource: CarrierChangeSource;
}

export const useCarrierSequence = ({
  proposals,
  coverageType,
  initialOrder,
  onOrderChange,
  lockedLeadingCarrierId,
  canonicalOrder,
}: UseCarrierSequenceOptions): UseCarrierSequenceReturn => {
  const [state, dispatch] = useReducer(
    carrierReorderReducer,
    initialOrder,
    createInitialCarrierReorderState,
  );
  const { sequenceOrder, pendingOrder, lastChangeSource, mutationInFlight } =
    state;

  const [lastValidationError, setLastValidationError] = useState<string | null>(
    null,
  );

  // Synchronous mirror of `mutationInFlight` — a dispatched action only takes
  // effect on the next render, so two dispatches issued back-to-back within
  // the same tick (e.g. AG Grid firing onColumnMoved twice) would both read
  // the pre-dispatch value from `state` otherwise. This ref is updated
  // manually, in lockstep with each dispatch, so the guard is correct within
  // a single synchronous block, not just across renders.
  const mutationInFlightRef = useRef(mutationInFlight);
  mutationInFlightRef.current = mutationInFlight;

  // Reset sequence order when coverageType changes so a previous coverage's
  // carrier IDs are not incorrectly reused for a different coverage.
  // initialOrder is read via a ref (kept fresh every render below) rather
  // than listed as an effect dependency, so a coverageType change resets
  // exactly once, using whatever initialOrder is current at that moment.
  //
  // The reducer rejects RESET while mutationInFlight is true (so a stray
  // reset can't race a commit that's still settling — see carrierReorderReducer).
  // If a coverageType change lands in that window, defer it: don't advance
  // prevCoverageTypeRef until the reset actually takes effect, and re-check
  // on every render where mutationInFlight changes (REARM will eventually
  // clear it) rather than silently dropping the reset.
  const prevCoverageTypeRef = useRef(coverageType);
  const initialOrderRef = useRef(initialOrder);
  initialOrderRef.current = initialOrder;
  useEffect(() => {
    if (prevCoverageTypeRef.current === coverageType) return;
    if (mutationInFlight) return;
    prevCoverageTypeRef.current = coverageType;
    dispatch({ type: "RESET", initialOrder: initialOrderRef.current });
    mutationInFlightRef.current = false;
  }, [coverageType, mutationInFlight]);

  const carrierSortInfo = useMemo(
    () => extractCarrierSortInfo(proposals, coverageType),
    [proposals, coverageType],
  );

  const defaultOrder = useMemo(
    () => carrierSortInfo.map((c) => c.carrierId),
    [carrierSortInfo],
  );

  // When this LOC has no custom order of its own, fall back to the
  // cross-LOC canonical order projected onto this LOC's actual carriers
  // (see projectCarrierOrder) rather than this LOC's own independent
  // default sort, so ordering stays consistent when switching between LOCs.
  const projectedDefaultOrder = useMemo(
    () =>
      canonicalOrder?.length
        ? projectCarrierOrder(canonicalOrder, defaultOrder)
        : defaultOrder,
    [canonicalOrder, defaultOrder],
  );

  const carrierIdOrder = useMemo(
    () =>
      sequenceOrder.carrierIds.length > 0
        ? sequenceOrder.carrierIds
        : projectedDefaultOrder,
    [sequenceOrder.carrierIds, projectedDefaultOrder],
  );

  const isCustomOrder = sequenceOrder.carrierIds.length > 0;

  // Whether the canonical-order projection actually changed anything from
  // this LOC's own plain default (as opposed to canonicalOrder being empty,
  // or containing none of this LOC's carriers, in which case the projection
  // is a no-op reshuffle that happens to land back in the same order).
  // Consumers use this (alongside isCustomOrder) to decide whether to pass
  // an explicit order to the grid, or let it use its own natural default.
  const isProjectedOrder = useMemo(
    () =>
      !isCustomOrder &&
      (projectedDefaultOrder.length !== defaultOrder.length ||
        projectedDefaultOrder.some((id, i) => id !== defaultOrder[i])),
    [isCustomOrder, projectedDefaultOrder, defaultOrder],
  );

  const requestReorder = useCallback(
    (
      carrierIds: string[],
      source: "drag" | "preset",
      movedCarrierId: string | null,
    ) => {
      // A commit is already in flight — reject rather than let this request
      // (a corrective AG Grid re-fire, or a genuine second drag that landed
      // before the previous one finished settling) race the pending one.
      if (mutationInFlightRef.current) return;
      mutationInFlightRef.current = true;
      dispatch({
        type: "REORDER_REQUESTED",
        order: { carrierIds, appliedPreset: null },
        source,
      });
      onOrderChange?.(carrierIds, source, movedCarrierId);
    },
    [onOrderChange],
  );

  const handleDragReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      setLastValidationError(null);
      try {
        const currentCarrier = carrierSortInfo.find((c) => c.isCurrent);
        if (currentCarrier) {
          const currentIndex = carrierIdOrder.indexOf(currentCarrier.carrierId);
          if (fromIndex === currentIndex) {
            setLastValidationError(
              "The current plan cannot be moved from its position.",
            );
            return;
          }
          if (toIndex === 0 && currentIndex === 0) {
            setLastValidationError(
              "No plan can be placed before the current plan.",
            );
            return;
          }
        }

        const movedCarrierId = carrierIdOrder[fromIndex] ?? null;
        const newOrder = arrayMove(carrierIdOrder, fromIndex, toIndex);
        requestReorder(newOrder, "drag", movedCarrierId);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Unknown error";
        setLastValidationError(`Failed to reorder carriers: ${message}`);
        logger.error(
          "[useCarrierSequence] handleDragReorder error:",
          error as Error,
        );
      }
    },
    [carrierIdOrder, carrierSortInfo, requestReorder],
  );

  const applyPreset = useCallback(
    (preset: CarrierSequencePreset) => {
      setLastValidationError(null);
      try {
        const newOrder = applyCarrierSequencePreset(preset, carrierSortInfo);
        // Presets can move every carrier at once, so there's no single
        // "moved carrier" to replay onto the cross-LOC order — the caller
        // falls back to its own pre-existing handling for this case.
        requestReorder(newOrder, "preset", null);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Unknown error";
        setLastValidationError(`Failed to apply preset: ${message}`);
        logger.error("[useCarrierSequence] applyPreset error:", error as Error);
      }
    },
    [carrierSortInfo, requestReorder],
  );

  const setCarrierOrder = useCallback(
    (carrierIds: string[], movedCarrierId: string | null = null) => {
      setLastValidationError(null);

      // If a locked leading carrier is configured, reject any reorder that
      // would displace it from the first position. This covers the case where
      // a real carrier is dragged past the synthetic Requested/Incumbent column.
      if (lockedLeadingCarrierId) {
        const lockedIdx = carrierIds.indexOf(lockedLeadingCarrierId);
        if (lockedIdx > 0) {
          setLastValidationError(
            "No carrier can be placed before the Incumbent/Requested column.",
          );
          return;
        }
      }

      // Strip the locked leading carrier from the order before saving — it is
      // always prepended separately when building grid data, so carrierIdOrder
      // should only contain real carrier IDs.
      const filteredIds = lockedLeadingCarrierId
        ? carrierIds.filter((id) => id !== lockedLeadingCarrierId)
        : carrierIds;

      const currentCarrier = carrierSortInfo.find((c) => c.isCurrent);
      if (
        currentCarrier &&
        defaultOrder[0] === currentCarrier.carrierId &&
        filteredIds[0] !== currentCarrier.carrierId
      ) {
        setLastValidationError(
          "The current plan cannot be moved from its position.",
        );
        return;
      }

      requestReorder(filteredIds, "drag", movedCarrierId);
    },
    [carrierSortInfo, defaultOrder, lockedLeadingCarrierId, requestReorder],
  );

  const resetOrder = useCallback(() => {
    setLastValidationError(null);
    // Reject a reset that lands while a commit hasn't settled yet — otherwise
    // a history-mode toggle mid-transition can repaint a stale default order
    // over a reorder that just committed.
    if (mutationInFlightRef.current) return;
    dispatch({ type: "RESET" });
  }, []);

  const clearValidationError = useCallback(() => {
    setLastValidationError(null);
  }, []);

  const hasPendingOrder = pendingOrder !== null;

  const commitPendingOrder = useCallback(() => {
    dispatch({ type: "COMMIT_PENDING" });
  }, []);

  const revertPendingOrder = useCallback(() => {
    // REVERT_PENDING is a no-op in the reducer when there's nothing pending
    // (e.g. a stray/redundant call) — only clear the ref when the dispatch
    // will actually clear mutationInFlight, otherwise the ref desyncs from
    // the reducer's real state and a subsequent requestReorder could call
    // onOrderChange even though the reducer will silently reject it.
    if (!pendingOrder) return;
    mutationInFlightRef.current = false;
    dispatch({ type: "REVERT_PENDING" });
  }, [pendingOrder]);

  // Rearm the in-flight guard after consumers have had one render to read
  // `lastChangeSource`. This is the same one-shot-flag reset the previous
  // implementation did, but it now runs through the reducer: a RESET or a
  // new REORDER_REQUESTED that lands before this fires is rejected instead
  // of racing the commit that's still settling (the CD-1419 fix).
  useEffect(() => {
    if (lastChangeSource !== "default") {
      mutationInFlightRef.current = false;
      dispatch({ type: "REARM" });
    }
  }, [lastChangeSource]);

  return {
    carrierIdOrder,
    appliedPreset: sequenceOrder.appliedPreset,
    handleDragReorder,
    setCarrierOrder,
    applyPreset,
    resetOrder,
    isCustomOrder,
    isProjectedOrder,
    lastValidationError,
    clearValidationError,
    hasPendingOrder,
    commitPendingOrder,
    revertPendingOrder,
    lastChangeSource,
  };
};
